import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArtifactSummary } from '../../types/artifact';
import { ArtifactCard } from './ArtifactCard';
import { ArtifactPreviewDrawer } from './ArtifactPreviewDrawer';
import { useTheme } from '../../contexts/ThemeContext';
import { FiGrid, FiList, FiCopy, FiExternalLink, FiCheck } from 'react-icons/fi';
import { relativeTime } from '../../utils/relativeTime';

type SortKey = 'newest' | 'oldest' | 'name_asc' | 'name_desc' | 'type';

const TYPE_EMOJI: Record<string, string> = {
  text: '📝', code: '💻', image: '🖼️', idea: '💡',
  critique: '🔍', solution: '✅', document: '📄', diagram: '📊',
  json: '🔧', yaml: '⚙️', toml: '🔩', csv: '🗃️', prompt: '✨',
};

const TYPE_ACCENT: Record<string, string> = {
  text: '#3b82f6', code: '#10b981', image: '#ec4899', idea: '#f59e0b',
  critique: '#ef4444', solution: '#6366f1', document: '#6b7280', diagram: '#14b8a6',
  json: '#f97316', yaml: '#06b6d4', toml: '#84cc16', csv: '#8b5cf6', prompt: '#ec4899',
};

function toMs(dateString: string | null | undefined): number {
  if (!dateString) return 0;
  const ms = new Date(dateString).getTime();
  return isNaN(ms) ? 0 : ms;
}

function sortArtifacts(artifacts: ArtifactSummary[], sort: SortKey): ArtifactSummary[] {
  console.debug(
    `[ArtifactBrowser] sortArtifacts — key="${sort}", count=${artifacts.length}`,
    artifacts.map(a => ({ name: a.name, created_at: a.created_at, ms: toMs(a.created_at) }))
  );
  switch (sort) {
    case 'newest':   return [...artifacts].sort((a, b) => toMs(b.created_at) - toMs(a.created_at));
    case 'oldest':   return [...artifacts].sort((a, b) => toMs(a.created_at) - toMs(b.created_at));
    case 'name_asc': return [...artifacts].sort((a, b) => a.name.localeCompare(b.name));
    case 'name_desc':return [...artifacts].sort((a, b) => b.name.localeCompare(a.name));
    case 'type':     return [...artifacts].sort((a, b) => a.type.localeCompare(b.type));
  }
}

interface ArtifactBrowserProps {
  artifacts: ArtifactSummary[];
  onArtifactClick?: (artifactId: string) => void;
}

export const ArtifactBrowser: React.FC<ArtifactBrowserProps> = ({ artifacts, onArtifactClick }) => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);

  const sorted = useMemo(() => {
    const result = sortArtifacts(artifacts, sortKey);
    console.debug(
      `[ArtifactBrowser] sorted result — key="${sortKey}"`,
      result.map(a => ({ name: a.name, created_at: a.created_at }))
    );
    return result;
  }, [artifacts, sortKey]);

  const handleCardClick = (artifactId: string) => {
    if (onArtifactClick) {
      onArtifactClick(artifactId);
    } else {
      setSelectedArtifactId(artifactId);
    }
  };

  if (artifacts.length === 0) {
    return (
      <div className={`text-center py-10 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No artifacts found</p>
      </div>
    );
  }

  return (
    <div>
      {/* Sticky toolbar */}
      <div className={`sticky top-0 z-10 flex items-center justify-between gap-3 px-3 py-2 mb-4 rounded-lg border ${
        darkMode
          ? 'bg-gray-900/90 border-gray-700 backdrop-blur'
          : 'bg-white/90 border-gray-200 backdrop-blur'
      }`}>
        {/* Result count */}
        <span className={`text-sm font-medium flex-shrink-0 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {artifacts.length} artifact{artifacts.length !== 1 ? 's' : ''}
        </span>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Sort dropdown */}
          <select
            value={sortKey}
            onChange={e => setSortKey(e.target.value as SortKey)}
            className={`text-xs px-2 py-1.5 rounded border ${
              darkMode
                ? 'bg-gray-800 border-gray-600 text-gray-200'
                : 'bg-white border-gray-300 text-gray-700'
            }`}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="name_asc">Name A→Z</option>
            <option value="name_desc">Name Z→A</option>
            <option value="type">Type</option>
          </select>

          {/* View toggle */}
          <div className={`inline-flex rounded-md border overflow-hidden ${
            darkMode ? 'border-gray-600' : 'border-gray-300'
          }`}>
            <button
              type="button"
              title="Grid view"
              className={`px-2.5 py-1.5 text-sm transition-colors ${
                viewMode === 'grid'
                  ? 'bg-blue-600 text-white'
                  : darkMode
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setViewMode('grid')}
            >
              <FiGrid className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="List view"
              className={`px-2.5 py-1.5 text-sm border-l transition-colors ${
                darkMode ? 'border-gray-600' : 'border-gray-300'
              } ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : darkMode
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setViewMode('list')}
            >
              <FiList className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Grid view */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map(artifact => (
            <ArtifactCard
              key={artifact.id}
              artifact={artifact}
              onClick={handleCardClick}
            />
          ))}
        </div>
      )}

      {/* List view */}
      {viewMode === 'list' && (
        <div className="space-y-1.5">
          {sorted.map(artifact => (
            <ListRow
              key={artifact.id}
              artifact={artifact}
              darkMode={darkMode}
              onClick={() => handleCardClick(artifact.id)}
              onOpen={() => navigate(`/artifacts/${artifact.id}`)}
            />
          ))}
        </div>
      )}

      {/* Preview drawer */}
      <ArtifactPreviewDrawer
        artifactId={selectedArtifactId}
        onClose={() => setSelectedArtifactId(null)}
        darkMode={darkMode}
      />
    </div>
  );
};

// ── List row sub-component ──────────────────────────────────────────────────

interface ListRowProps {
  artifact: ArtifactSummary;
  darkMode: boolean;
  onClick: () => void;
  onOpen: () => void;
}

const ListRow: React.FC<ListRowProps> = ({ artifact, darkMode, onClick, onOpen }) => {
  const [hovered, setHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const accent = TYPE_ACCENT[artifact.type] ?? '#6b7280';
  const emoji = TYPE_EMOJI[artifact.type] ?? '📄';

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = typeof (artifact as any).content === 'string'
      ? (artifact as any).content
      : artifact.name;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpen();
  };

  return (
    <div
      className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
        darkMode
          ? 'bg-gray-800 border-gray-700 hover:border-gray-500'
          : 'bg-white border-gray-200 hover:border-gray-400'
      }`}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Accent bar */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
        style={{ backgroundColor: accent }}
      />

      <span className="text-lg flex-shrink-0 ml-1">{emoji}</span>

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          {artifact.name}
        </p>
        <div className={`flex items-center gap-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span>{artifact.type}</span>
          {artifact.phase && <><span>•</span><span>{artifact.phase}</span></>}
          {artifact.agent_name && <><span>•</span><span>By {artifact.agent_name}</span></>}
        </div>
      </div>

      <span className={`text-xs flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        {relativeTime(artifact.created_at)}
      </span>

      {/* Hover quick-actions */}
      <div className={`flex items-center gap-1.5 transition-all duration-150 ${
        hovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}>
        <button
          onClick={handleCopy}
          title="Copy content"
          className={`p-1.5 rounded transition-colors ${
            darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}
        >
          {copied ? <FiCheck className="w-3.5 h-3.5 text-green-500" /> : <FiCopy className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={handleOpen}
          title="Open full page"
          className={`p-1.5 rounded transition-colors ${
            darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}
        >
          <FiExternalLink className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
